import { useState, useCallback, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PasswordInput } from '../../components/ui/PasswordInput'
import { AuthLayout, authFormStyles as s } from '@/components/auth/AuthLayout'
import { ApiError } from '@/api/httpClient'
import { LoadingScreen } from '@/components/auth/LoadingScreen'
import { useAuthStore } from '@/stores/authStore'

export default function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const login = useAuthStore((st) => st.login)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showLoader, setShowLoader] = useState(false)

  const handleLoaderDone = useCallback(() => {
    navigate('/workspaces', { replace: true })
  }, [navigate])

  /**
   * Credenciais invalidas chegam como 401 (`NO_ACTIVE_ACCOUNT` do simplejwt),
   * cuja mensagem crua fala de "conta ativa" e confunde quem so errou a senha.
   */
  function loginErrorMessage(err: unknown): string {
    if (err instanceof ApiError && (err.status === 401 || err.code === 'NO_ACTIVE_ACCOUNT')) {
      return t('auth.login.invalidCredentials', 'E-mail ou senha incorretos.')
    }
    if (err instanceof Error) return err.message
    return String(err)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await login({ email, password })
      setShowLoader(true)
    } catch (err) {
      setError(loginErrorMessage(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
    {showLoader && <LoadingScreen onDone={handleLoaderDone} />}
    <AuthLayout
      title={t('auth.login.title', 'Entrar')}
      subtitle={t('auth.login.subtitle', 'Acesse sua conta MorphERD.')}
      footer={
        <span>
          {t('auth.login.noAccount', 'Nao tem conta?')}{' '}
          <Link to="/register" style={{ color: 'var(--accent)' }}>
            {t('auth.login.register', 'Registrar')}
          </Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error ? <div style={s.error}>{error}</div> : null}
        <div style={s.field}>
          <label style={s.label} htmlFor="email">{t('auth.fields.email', 'E-mail')}</label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={s.input}
          />
        </div>
        <div style={s.field}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
            <label style={s.label} htmlFor="password">{t('auth.fields.password', 'Senha')}</label>
            <Link
              to={email ? `/forgot-password?email=${encodeURIComponent(email)}` : '/forgot-password'}
              style={{ color: 'var(--accent)', fontSize: 13 }}
            >
              {t('auth.login.forgotPassword', 'Esqueci minha senha')}
            </Link>
          </div>
          <PasswordInput
            id="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={s.input}
          />
        </div>
        <button type="submit" style={s.button} disabled={submitting}>
          {submitting ? t('auth.login.submitting', 'Entrando...') : t('auth.login.submit', 'Entrar')}
        </button>
      </form>
    </AuthLayout>
    </>
  )
}
